import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useDashboardContext } from '../contexts/DashboardContext';
import { useModal } from '../contexts/ModalContext';
import { DashboardHeader } from '../components/dna/DashboardHeader';
import { getStatusLabel, canShareRelease } from '../constants/forms';
import { authFetch } from '../services/api';
import { handleImageError } from '../utils/imageUtils';
import { coverGradientFor } from '../utils/coverGradient';

const Releases = () => {
  const { toggleMobileMenu } = useOutletContext();
  const {
    data,
    openCreateRelease,
    openViewRelease,
    openEditRelease,
    handleDeleteRelease,
  } = useDashboardContext();
  const { confirmDelete } = useModal();
  const [shareMessage, setShareMessage] = useState('');

  const [quota, setQuota] = useState(null);
  const loadQuota = useCallback(async () => {
    try {
      const res = await authFetch('/api/subscriptions/status');
      if (res.ok) {
        const d = await res.json();
        if (d.system_enabled) setQuota(d);
      }
    } catch { /* silence */ }
  }, []);
  useEffect(() => { loadQuota(); }, [loadQuota]);
  useEffect(() => {
    const h = () => loadQuota();
    window.addEventListener('passio-upload-success', h);
    return () => window.removeEventListener('passio-upload-success', h);
  }, [loadQuota]);

  const tracksExhausted = quota && (quota.tracks_remaining ?? 0) <= 0;
  const quotaTooltip = tracksExhausted && quota
    ? `Quota mensuel atteint (${quota.tracks_used_this_month || 0}/${quota.limit_this_month || 3})`
    : '';

  const onDeleteRelease = async (album) => {
    await confirmDelete({
      itemName: album.title,
      onConfirm: async () => { await handleDeleteRelease(album); },
    });
  };

  const handleShare = async (album) => {
    const artistSlug = data?.artist?.slug;
    const albumSlug = album?.slug;
    if (!artistSlug || !albumSlug) return;
    const baseUrl = album?.is_free ? 'https://player.passiio.shop' : 'https://passiio.shop';
    try {
      await navigator.clipboard.writeText(`${baseUrl}/p/${artistSlug}/${albumSlug}`);
      setShareMessage('Lien copié !');
      setTimeout(() => setShareMessage(''), 3000);
    } catch { /* silence */ }
  };

  const albums = data?.albums || [];

  return (
    <>
      <DashboardHeader
        title="Mes sorties"
        subtitle={`Gérez vos publications · ${new Date().getFullYear()}`}
        onMenu={toggleMobileMenu}
      />
      <div className="dashboard-content">
      <div className="page-toolbar">
        <span className="content-kicker">{albums.length} sortie{albums.length > 1 ? 's' : ''}</span>
        <button
          className="button button-dark"
          onClick={tracksExhausted ? undefined : openCreateRelease}
          disabled={tracksExhausted}
          title={quotaTooltip}
          style={tracksExhausted ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
        >
          <i className="fa-solid fa-plus" /> Ajouter une sortie
        </button>
      </div>

      {albums.length === 0 ? (
        <div className="community-placeholder">
          <span className="content-kicker">Catalogue vide</span>
          <h2>Aucune sortie pour le moment.</h2>
          <p>Publie ton premier single ou album pour le voir ici.</p>
          <button className="button button-dark" onClick={openCreateRelease}>
            <i className="fa-solid fa-plus" /> Créer une sortie
          </button>
        </div>
      ) : (
        <div className="release-list">
          {albums.map((album) => (
            <article className="release-row" key={album.id} onClick={() => openViewRelease(album)}>
              <span className="release-row-cover" style={album.cover_url ? undefined : { background: coverGradientFor(album.id) }}>
                {album.cover_url ? (
                  <img src={album.cover_url} alt={album.title} onError={handleImageError} />
                ) : (
                  <span aria-hidden="true">{(album.title || 'P')[0].toUpperCase()}</span>
                )}
              </span>
              <span className="release-row-main">
                <b>{album.title}</b>
                <small>
                  <span className="release-type">{album.type === 'single' ? 'Single' : 'Album'}</span>
                  <span className={`status status-${String(album.status || 'draft')}`}>{getStatusLabel(album.status)}</span>
                </small>
              </span>
              <span className="release-row-meta">
                <b>{Number(album.price_ariary) !== 0 ? `${Number(album.price_ariary || 0).toLocaleString()} Ar` : 'Gratuit'}</b>
              </span>
              <span className="release-row-actions" onClick={(e) => e.stopPropagation()}>
                {canShareRelease(album.status) && (
                  <button title="Partager" onClick={() => handleShare(album)}>
                    <i className="fa-solid fa-share-nodes" />
                  </button>
                )}
                <button title="Modifier" onClick={() => openEditRelease(album)}>
                  <i className="fa-solid fa-pen" />
                </button>
                <button title="Supprimer" className="danger" onClick={() => onDeleteRelease(album)}>
                  <i className="fa-solid fa-trash" />
                </button>
              </span>
            </article>
          ))}
        </div>
      )}

      {shareMessage && (
        <div style={{
          position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
          background: '#189b69', color: '#fff', padding: '10px 20px',
          borderRadius: '10px', zIndex: 1000, boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}>
          {shareMessage}
        </div>
      )}

      {/* Bouton bas : version mobile uniquement (masqué sur desktop en CSS) */}
      <button
        className="button button-dark add-release-bottom"
        onClick={tracksExhausted ? undefined : openCreateRelease}
        disabled={tracksExhausted}
        title={quotaTooltip}
        style={tracksExhausted ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
      >
        <span>Ajouter une sortie</span>
        <span className="add-release-plus" aria-hidden="true">
          <i className="fa-solid fa-plus" />
        </span>
      </button>
      </div>
    </>
  );
};

export default Releases;
